// Contactos del iPhone (o de iCloud) exportados como vCard (.vcf): se convierten en una hoja con nombre, WhatsApp,
// correo y notas, para importarlos como los Excel. Solo se leen esos datos; la foto y lo demás se ignoran.
import type { HojaLeida } from './xlsx';

interface Propiedad { nombre: string; params: string[]; valor: string }

// Una línea larga del vCard sigue en la siguiente si esta empieza con espacio o tabulación.
const desplegar = (t: string) => t.replace(/\r\n/g, '\n').replace(/\n[ \t]/g, '');
const sinEscapes = (v: string) => v.replace(/\\n/gi, ' ').replace(/\\([,;\\])/g, '$1').trim();
function quotedPrintable(v: string): string {
  const bytes: number[] = [];
  const s = v.replace(/=\n/g, '');
  for (let i = 0; i < s.length; i++) {
    if (s[i] === '=' && /^[0-9A-F]{2}$/i.test(s.slice(i + 1, i + 3))) { bytes.push(parseInt(s.slice(i + 1, i + 3), 16)); i += 2; }
    else bytes.push(...new TextEncoder().encode(s[i]));
  }
  return new TextDecoder().decode(new Uint8Array(bytes));
}
function propiedad(linea: string): Propiedad | null {
  const i = linea.indexOf(':');
  if (i < 0) return null;
  const [cabeza, ...params] = linea.slice(0, i).split(';');
  let valor = linea.slice(i + 1);
  if (params.some(p => /encoding=quoted-printable/i.test(p))) valor = quotedPrintable(valor);
  return { nombre: cabeza.replace(/^[^.]*\./, '').toUpperCase(), params: params.map(p => p.toLowerCase()), valor };
}

// El celular primero (móvil, iPhone, WhatsApp), luego el preferido, luego el primero que haya.
function mejorTelefono(tels: { valor: string; tipo: string }[]): string {
  const movil = tels.find(t => /cell|iphone|mobile|movil|móvil|celular|whatsapp/i.test(t.tipo)), pref = tels.find(t => /pref/.test(t.tipo));
  return (movil || pref || tels[0])?.valor || '';
}

export function leerVcf(texto: string, nombre = 'Contactos'): HojaLeida {
  const filas: (string | null)[][] = [['Nombre', 'WhatsApp', 'Correo', 'Notas']];
  const lineas = desplegar(texto.replace(/^﻿/, '')).split('\n');
  let actual: { lineas: string[] } | null = null;
  for (const l of lineas) {
    if (/^BEGIN:VCARD/i.test(l)) { actual = { lineas: [] }; continue; }
    if (/^END:VCARD/i.test(l)) {
      if (actual) { const f = contacto(actual.lineas); if (f) filas.push(f); }
      actual = null; continue;
    }
    actual?.lineas.push(l);
  }
  return { nombre, filas };
}

function contacto(lineas: string[]): (string | null)[] | null {
  // "item1.TEL:…" y "item1.X-ABLabel:WhatsApp": la etiqueta propia del iPhone va en una línea aparte del mismo grupo.
  const props = lineas.flatMap(l => { const p = propiedad(l); return p ? [{ ...p, grupo: l.match(/^([A-Za-z0-9-]+)\.[A-Za-z]/)?.[1].toLowerCase() || '' }] : []; });
  const de = (n: string) => props.filter(p => p.nombre === n);
  if (de('X-ADDRESSBOOKSERVER-KIND').some(p => /group/i.test(p.valor))) return null; // listas de iCloud, no son personas
  const etiqueta = (g: string) => (g ? props.find(p => p.grupo === g && p.nombre === 'X-ABLABEL')?.valor || '' : '');
  const tel = mejorTelefono(de('TEL').map(p => ({ valor: p.valor.trim(), tipo: p.params.join(';') + ';' + etiqueta(p.grupo) })));
  const n = de('N')[0]?.valor.split(';').map(sinEscapes) || [];
  const nombre = sinEscapes(de('FN')[0]?.valor || '') || [n[1], n[2], n[0]].filter(Boolean).join(' ') || sinEscapes(de('ORG')[0]?.valor.split(';')[0] || '');
  if (!tel && !nombre) return null;
  return [nombre, tel, sinEscapes(de('EMAIL')[0]?.valor || ''), sinEscapes(de('NOTE')[0]?.valor || '')];
}
